/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { NavigationItem } from '@squiz/sds';
import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Dxp404 } from '../../Components/Dxp404/Dxp404';
import { DxpErrorScreen } from '../../Screens/DxpErrorScreen/DxpErrorScreen';
import { DxpLogin } from '../../Screens/DxpLogin/DxpLogin';
import { Logout } from '../../Screens/Logout/Logout';
import { replaceTenantParam } from '../../Utilities/ReplaceTenantParam/ReplaceTenantParam';
import { DxpLoginAuthorize } from '../DxpLoginAuthorize/DxpLoginAuthorize';
import { DxpLoginRedirect } from '../DxpLoginRedirect/DxpLoginRedirect';
import { DxpSecureRoute } from '../DxpSecureRoute/DxpSecureRoute';
import { IdpLoginAuthorize } from '../IdpLoginAuthorize/IdpLoginAuthorize';
import { DXP_ROUTE_REDIRECTS } from './DxpRoutes.constants';
import { RoutesProps } from './DxpRoutes.props';

/**
 * DXP Console Router.
 */
export const DxpRoutes = (props: RoutesProps): JSX.Element => {
  const { items } = props;

  const addRedirect = (redirectFrom: string, redirectTo: string): JSX.Element => {
    const redirectElement = (
      <DxpSecureRoute>
        <Navigate replace to={redirectTo} />
      </DxpSecureRoute>
    );

    return <Route element={redirectElement} key={redirectFrom} path={redirectFrom} />;
  };

  // Build the routes that have a valid path
  const appRoutes = items
    .filter((item: NavigationItem) => item.path !== ``)
    .map((item: NavigationItem): JSX.Element => {
      let element = item.screen;

      // If the route has children, redirect to the first child
      if (item.children) {
        const redirectFrom = item.path;
        const redirectTo = replaceTenantParam(item.children[0].path);
        return addRedirect(redirectFrom, redirectTo);
      }

      // If it's not a public screen, wrap it in the secure router
      if (!item.isPublic) {
        element = <DxpSecureRoute item={item}>{item.screen}</DxpSecureRoute>;
      }

      return <Route element={element} key={item.key} path={item.path} />;
    });

  // Handle redirects
  const appRedirects = Object.keys(DXP_ROUTE_REDIRECTS).map((item) => {
    const redirectFrom = item;
    const redirectTo = replaceTenantParam(DXP_ROUTE_REDIRECTS[item]);
    return addRedirect(redirectFrom, redirectTo);
  });

  return (
    <Routes>
      {appRoutes}
      {appRedirects}
      <Route element={<DxpLogin />} path="/login" />
      <Route element={<Logout />} path="/logout" />
      <Route element={<DxpLoginRedirect />} path="/dxp-login" />
      <Route element={<DxpLoginAuthorize />} path="/authorize/*" />
      <Route element={<IdpLoginAuthorize />} path="/idp-authorize/*" />
      <Route element={<DxpErrorScreen />} path="/error" />
      <Route element={<Dxp404 />} path="*" />
    </Routes>
  );
};
